


















































import { Component, Prop, Vue } from 'vue-property-decorator';

import Swal from 'sweetalert2';

import { eventBus, EventType } from '../eventBus';

import {
  AssessmentInstance,
  AssessmentInstanceAnswer,
  Question,
  QuestionOption,
  Section,
} from '../models';

import {
  getLastAnsweredQuestionId,
  getNextUnansweredQuestionId,
  getPreviousQuestionId,
  getSelectedOptionIdsUpToQuestion,
  httpPost,
} from '../utils';

@Component
export default class QuestionContainer extends Vue {
  @Prop(Object) private readonly assessment!: AssessmentInstance;

  private isLoading: boolean = true;
  private currentQuestionId: number = -1;
  private initiallySelectedOptionId: number = -1;
  private currentlySelectedOptionId: number = -1;
  private confirmationMessage: string = 'Changing your answer to this question will change subsequent questions. ' +
    'You will lose any existing answers to subsequent questions. Are you sure you want to change your answer?';

  get assessmentId(): number {
    return parseInt(this.$route.params.assessmentId, 10);
  }

  get sectionId(): number {
    return parseInt(this.$route.params.sectionId, 10);
  }

  get section(): Section {
    return this.assessment.sections.find((s) => s.id === this.sectionId)!;
  }

  get sortedSections(): Section[] {
    return this.assessment.sections.sort((s1, s2) => s1.sortOrder - s2.sortOrder);
  }

  get nextSectionIndex(): number {
    const currentSectionIndex = this.sortedSections.findIndex((s) => s.id === this.sectionId);
    if (currentSectionIndex + 1 === this.sortedSections.length) {
      return -1;
    } else {
      return currentSectionIndex + 1;
    }
  }

  get currentQuestion(): Question {
    return this.section.questions.find((q) => q.id === this.currentQuestionId)!;
  }

  get options(): QuestionOption[] {
    return this.section.questions.flatMap((q) => q.options);
  }

  get initiallySelectedOption(): QuestionOption {
    return this.options.find((o) => o.id === this.initiallySelectedOptionId)!;
  }

  get currentlySelectedOption(): QuestionOption {
    return this.options.find((o) => o.id === this.currentlySelectedOptionId)!;
  }

  get showConfirmationOnNext(): boolean {
    return this.initiallySelectedOptionId !== -1 &&
      (this.currentlySelectedOption.nextQuestionId !== this.initiallySelectedOption.nextQuestionId);
  }

  get disablePreviousButton(): boolean {
    return this.currentQuestion.isFirstQuestion;
  }

  get disableNextButton(): boolean {
    return this.currentlySelectedOptionId === -1 || (this.currentlySelectedOption.nextQuestionId === -1 &&
      this.currentlySelectedOptionId === this.initiallySelectedOptionId);
  }

  get nextButtonLabel(): string {
    if (this.currentlySelectedOptionId !== -1 && this.currentlySelectedOption.nextQuestionId === -1) {
      return 'Complete Section';
    }
    return 'Next Question';
  }

  private created() {
    this.isLoading = true;
    this.currentQuestionId = getNextUnansweredQuestionId(this.assessment, this.sectionId);
    if (this.currentQuestionId === -1) {
      this.currentQuestionId = getLastAnsweredQuestionId(this.assessment, this.sectionId);
    }
    this.preselectOption();
    this.isLoading = false;
  }

  private preselectOption() {
    const selectedOptionIds = this.assessment.answers.map((a) => a.questionOptionId);
    for (const option of this.currentQuestion.options) {
      if (selectedOptionIds.includes(option.id)) {
        this.initiallySelectedOptionId = option.id;
        this.currentlySelectedOptionId = option.id;
        return;
      }
    }
    this.initiallySelectedOptionId = -1;
    this.currentlySelectedOptionId = -1;
  }

  private showPreviousQuestion() {
    if (this.disablePreviousButton) {
      return;
    }
    this.currentQuestionId = getPreviousQuestionId(this.assessment, this.sectionId, this.currentQuestionId);
    this.preselectOption();
  }

  private showNextQuestion() {
    if (this.disableNextButton) {
      return;
    }
    if (this.currentlySelectedOptionId === this.initiallySelectedOptionId) {
      if (this.currentlySelectedOption.nextQuestionId !== -1) {
        this.currentQuestionId = this.currentlySelectedOption.nextQuestionId;
        this.preselectOption();
      }
    } else {
      if (this.showConfirmationOnNext) {
        Swal.fire({
          title: 'Are you sure?',
          text: this.confirmationMessage,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Change Answer',
          cancelButtonText: 'Cancel',
          focusCancel: true,
        }).then((result) => {
          if (result.value) {
            this.saveAnswer();
          }
        });
      } else {
        this.saveAnswer();
      }
    }
  }

  private saveAnswer() {
    const optionIds = getSelectedOptionIdsUpToQuestion(this.assessment, this.sectionId, this.currentQuestionId);
    optionIds.push(this.currentlySelectedOptionId);
    const url = `${process.env.VUE_APP_API_HOST}/assessments/${this.assessmentId}/sections/${this.sectionId}`;
    this.isLoading = true;
    httpPost<AssessmentInstanceAnswer[]>(url, optionIds)
      .then((answers) => {
        this.assessment.answers = answers;
        const nextUnansweredQuestionId = getNextUnansweredQuestionId(this.assessment, this.sectionId);
        if (nextUnansweredQuestionId === -1) {
          const alertText = this.nextSectionIndex === -1 ?
            'Continue to the threats page?' : 'Continue to the next section?';
          Swal.fire({
            title: 'Section Completed',
            type: 'success',
            text: alertText,
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
          }).then((result) => {
            if (result.value) {
              if (this.nextSectionIndex === -1) {
                const firstSection = this.sortedSections[0];
                this.$router.push({
                  name: 'threats-section',
                  params: {
                    sectionId: `${firstSection.id}`,
                  },
                });
              } else {
                const nextSection = this.sortedSections[this.nextSectionIndex];
                this.$router.push({
                  name: 'questions-section',
                  params: {
                    sectionId: `${nextSection.id}`,
                  },
                });
              }
            }
          });
          eventBus.$emit(EventType.SECTION_COMPLETED, this.sectionId);
        } else {
          this.currentQuestionId = nextUnansweredQuestionId;
          this.preselectOption();
          eventBus.$emit(EventType.SECTION_MADE_PROGRESS, this.sectionId);
        }
      }).catch((error) => {
        Swal.fire({
          title: 'Save Failed',
          text: `Error: ${error.message}`,
          type: 'error',
        });
      }).then(() => {
        this.isLoading = false;
      });
  }
}
